// ROUTE_RULES — the deterministic concept-matching ruleset, ported verbatim
// from the original app (rule-engine reference §1). Each rule has a level
// (ie > nc > hk), a human label, anchors, and concept groups (AND-sets of
// substrings that are OR'd via summed scoring). `bbbbee` (4 b's) is intentional
// — the synonym map folds bbbee/b-bbee/bbee → bbbbee.

export type RouteKey = 'rfq' | 'lpo' | 'tender' | 'emergency' | 'urgent' | 'generic';
export type RuleLevel = 'ie' | 'nc' | 'hk';

export interface Rule {
  level: RuleLevel;
  label: string;
  anchors: string[];
  concepts: string[][];
}

export const ROUTE_RULES: Record<RouteKey, Rule[]> = {
  rfq: [
    {
      level: 'ie',
      label: 'RFQ value exceeds threshold / wrong route used',
      anchors: ['PFMA', 'Eskom Policies & Procedures'],
      concepts: [
        ['rfq', 'r1m'],
        ['rfq', 'formal tender'],
        ['threshold', 'rfq'],
        ['wrong route', 'rfq'],
        ['quotation', 'over r1m'],
      ],
    },
    {
      level: 'ie',
      label: 'RFQ not issued via CSD or valid deviation absent',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['rfq', 'csd'],
        ['quotation', 'outside csd'],
        ['deviation', 'missing'],
        ['csd', 'screenshot'],
        ['csd', 'unavailable'],
      ],
    },
    {
      level: 'ie',
      label: 'Award not aligned to DAA / highest scorer / validity / tax',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['daa', 'approval'],
        ['highest scorer'],
        ['validity', 'expired'],
        ['tax', 'compliance'],
        ['market related', 'price'],
      ],
    },
    {
      level: 'nc',
      label: 'RFQ evaluation, COI or negotiation pack missing',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['evaluation', 'signature'],
        ['coi', 'missing'],
        ['conflict of interest'],
        ['negotiation', 'minutes'],
        ['supplier hierarchy'],
      ],
    },
    {
      level: 'hk',
      label: 'RFQ returnables or supplier invitation count issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['sbd4', 'missing'],
        ['integrity pact', 'missing'],
        ['shareholding', 'missing'],
        ['returnables', 'missing'],
        ['6 suppliers'],
      ],
    },
  ],
  lpo: [
    {
      level: 'ie',
      label: 'LPO threshold, modification or forex issue',
      anchors: ['PFMA', 'Eskom Policies & Procedures'],
      concepts: [
        ['lpo', 'r30000'],
        ['lpo', 'threshold'],
        ['lpo', 'modified'],
        ['forex', 'lpo'],
        ['change order', 'lpo'],
      ],
    },
    {
      level: 'ie',
      label: 'LPO quotation, hierarchy, tax or market price missing',
      anchors: ['PFMA', 'Eskom Policies & Procedures'],
      concepts: [
        ['quotation', 'missing'],
        ['supplier hierarchy'],
        ['tax', 'compliance'],
        ['market related', 'price'],
        ['highest ranked'],
      ],
    },
    {
      level: 'nc',
      label: 'LPO misuse / PR / EDMS / approval issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['edms', 'missing'],
        ['purchase requisition'],
        ['written request'],
        ['complex goods'],
        ['existing contract'],
        ['once off'],
        ['stock item'],
        ['cpa', 'lpo'],
      ],
    },
    {
      level: 'hk',
      label: 'LPO forms / supplier count housekeeping',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['fewer than 3 suppliers'],
        ['less than 3 suppliers'],
        ['below 3 suppliers'],
        ['minimum 3 suppliers not sourced'],
        ['sbd4', 'missing'],
        ['authorisation form', 'missing'],
        ['integrity declaration', 'missing'],
        ['shareholding', 'missing'],
      ],
    },
  ],
  tender: [
    {
      level: 'ie',
      label: 'Tender strategy / DAA / evaluation / award breach',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['strategy', 'daa'],
        ['evaluation', 'strategy'],
        ['adjudication', 'daa'],
        ['award', 'validity'],
        ['tax', 'compliance'],
        ['nt etender'],
        ['cidb'],
      ],
    },
    {
      level: 'ie',
      label: 'Tender publications, clarification or cancellation non-compliance',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['published', 'portal'],
        ['nt etender'],
        ['clarification', 'all bidders'],
        ['cancellation', 'approval'],
        ['reported', 'successful'],
      ],
    },
    {
      level: 'nc',
      label: 'Tender plan, appointments, COI/NDA, negotiation pack or EDMS issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['procurement plan'],
        ['appointment letters'],
        ['coi'],
        ['nda'],
        ['negotiation pack'],
        ['edms'],
        ['invitation letter'],
      ],
    },
    {
      level: 'hk',
      label: 'Tender administrative filing issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [['wrong folder'], ['incorrect reference'], ['filing issue'], ['document management']],
    },
  ],
  emergency: [
    {
      level: 'ie',
      label: 'Emergency did not qualify / urgency not demonstrated / reporting issue',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['not emergency'],
        ['immediate risk'],
        ['urgent action'],
        ['competitive bidding'],
        ['quotation before approval'],
        ['agsa'],
        ['ocpo'],
      ],
    },
    {
      level: 'ie',
      label: 'Emergency award / DAA / tax / BBBEE / CSD issue',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['daa', 'ratification'],
        ['bbbbee'],
        ['csd report'],
        ['tax', 'compliance'],
        ['integrity pact'],
        ['sbd4'],
      ],
    },
    {
      level: 'nc',
      label: 'Emergency request form / mitigation / SAP / EDMS issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['request form'],
        ['mitigation'],
        ['existing contract'],
        ['submission report'],
        ['zem pr'],
        ['supporting documentation'],
        ['edms'],
      ],
    },
  ],
  urgent: [
    {
      level: 'ie',
      label: 'Urgent procurement did not qualify / planning failure / bidding possible',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['critical delivery'],
        ['competitive bidding'],
        ['lack of planning'],
        ['foreseeable'],
        ['qualifying circumstance'],
        ['quotation before approval'],
        ['reported', 'agsa'],
      ],
    },
    {
      level: 'ie',
      label: 'Urgent award / DAA / COI / BBBEE / CSD / tax issue',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['daa', 'approval'],
        ['coi'],
        ['negotiation pack'],
        ['bbbbee'],
        ['csd report'],
        ['tax', 'compliance'],
        ['sbd4'],
      ],
    },
    {
      level: 'nc',
      label: 'Urgent request form / contract duration / PR / EDMS issue',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['request form'],
        ['12 months'],
        ['existing contract'],
        ['supporting documents'],
        ['edms'],
        ['change order'],
      ],
    },
  ],
  generic: [
    {
      level: 'ie',
      label: 'Generic possible irregular expenditure trigger',
      anchors: ['PFMA', 'National Treasury', 'Eskom Policies & Procedures'],
      concepts: [
        ['pfma'],
        ['possible ie'],
        ['irregular expenditure'],
        ['national treasury'],
        ['constitution 217'],
        ['award outside validity'],
        ['tax', 'non compliant'],
      ],
    },
    {
      level: 'nc',
      label: 'Generic policy and procedure control gap',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['procurement plan'],
        ['appointment'],
        ['coi'],
        ['doi'],
        ['nda'],
        ['evaluation'],
        ['adjudication'],
        ['negotiation'],
        ['daa'],
      ],
    },
    {
      level: 'hk',
      label: 'Generic administrative or filing gap',
      anchors: ['Eskom Policies & Procedures'],
      concepts: [
        ['edms'],
        ['opentext'],
        ['filing'],
        ['wrong report'],
        ['administrative'],
        ['housekeeping'],
        ['document management'],
      ],
    },
  ],
};

// NB (Jun 11 2026 call): statutory-document anchoring is handled by the GUARANTEED
// FALLBACK in classify.ts (which fires only when no rule scored ≥2), NOT by a
// scoring rule here. An earlier attempt added a high-scoring statutory rule whose
// summed single-token concepts crowded a genuine `ie` route rule out of the keep-
// window — downgrading Possible-IE findings. The fallback anchors a *pure*
// statutory finding to STATUTORY_RULE_LABEL + its references without ever
// competing against (and downgrading) a higher-severity match. See
// src/domain/rulebook/ruleReferences.ts → 'Statutory documents not provided / verified'.
